import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/shared/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import API from '../utils/api';
import { trackStoryGenerated } from '../utils/analytics';
import useLimits from '../utils/useLimits';
import useSettings from '../utils/useSettings';
import TopUpModal from '../components/TopUpModal';
import { getSeasonalContext, HOLIDAYS } from '../utils/seasonalCalendar';
import ThemeVotingCard from '../components/ThemeVotingCard';
import { canGenerateStories, entitledPlan } from '../utils/entitlements';

const FREE_THEMES = ['Kindness', 'Bravery', 'Friendship'];

// ── Progress messages cycle during story generation ───────────────────────────
const PROGRESS_STEPS = [
  { pct: 0,  msg: 'Starting the adventure…' },
  { pct: 12, msg: 'Dreaming up the perfect plot…' },
  { pct: 25, msg: 'Adding your child\'s favourite things…' },
  { pct: 38, msg: 'Writing the exciting scenes…' },
  { pct: 52, msg: 'Giving the characters their own voices…' },
  { pct: 65, msg: 'Polishing every sentence…' },
  { pct: 78, msg: 'Weaving in the life lesson…' },
  { pct: 88, msg: 'Creating the illustration…' },
  { pct: 94, msg: 'Almost ready — finishing touches…' },
];

const GenerateStoryPage = () => {
  const { darkMode: dm } = useTheme();
  const [searchParams] = useSearchParams();
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [selectedChildren, setSelectedChildren] = useState([]); // multi-child mode
  const [multiChildMode, setMultiChildMode] = useState(false);
  const [interests, setInterests] = useState([]);
  const [themes, setThemes] = useState([]);
  const [tone, setTone] = useState('');
  const [language, setLanguage] = useState('en');
  const [customPrompt, setCustomPrompt] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(0);
  const [genMsg, setGenMsg] = useState(PROGRESS_STEPS[0].msg);
  const genProgressRef = useRef(null);
  const [error, setError] = useState('');
  const [loadingChildren, setLoadingChildren] = useState(true);
  const [activeThemeTab, setActiveThemeTab] = useState(0);
  const [showTopUp, setShowTopUp] = useState(false);
  const [seasonalApplied, setSeasonalApplied] = useState(false);
  const [learningTopic, setLearningTopic] = useState('');
  const [learningTab, setLearningTab] = useState(0);
  const [customLearningTopic, setCustomLearningTopic] = useState('');
  const [availableCharacters, setAvailableCharacters] = useState([]);
  const [selectedCharacterIds, setSelectedCharacterIds] = useState([]);
  const [charactersLocked, setCharactersLocked] = useState(false);
  const [autoStartPending, setAutoStartPending] = useState(false);
  const autoStartFiredRef = useRef(false);
  const [showThemeVoting, setShowThemeVoting] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const hasPaidPlan = entitledPlan(user) !== 'free';
  const { usage, limits, remaining, refresh: refreshLimits } = useLimits();
  const storiesOut = remaining.stories <= 0;
  const { settings: siteSettings } = useSettings();
  const TONES              = siteSettings.storyTones;
  const LANGUAGES          = siteSettings.storyLanguages;
  const INTERESTS          = siteSettings.storyInterests;
  const LEARNING_CATEGORIES = siteSettings.learningCategories;
  const THEME_CATEGORIES   = siteSettings.themeCategories;

  // Seasonal context — shown to all plans
  const seasonal = getSeasonalContext();
  const seasonalHoliday = seasonal.active || seasonal.upcoming?.holiday || null;
  const seasonalDaysAway = seasonal.upcoming?.daysAway || null;

  // Clean up progress interval on unmount
  useEffect(() => () => clearInterval(genProgressRef.current), []);

  useEffect(() => {
    const isAutostart = !!searchParams.get('autostart');
    const preselectId = searchParams.get('childId');
    API.get('/children').then(res => {
      setChildren(res.data.children);
      if (res.data.children.length > 0 && !isAutostart) {
        // Deep links can preselect a child via ?childId=
        const match = preselectId && res.data.children.find(c => c._id === preselectId);
        setSelectedChild(match ? match._id : res.data.children[0]._id);
      }
    }).catch(err => {
      // Otherwise a load failure renders "Create a child profile first" to a
      // parent who already has profiles, bouncing them out of the main flow.
      setError(err.response?.data?.message || 'Could not load your children. Please refresh and try again.');
    }).finally(() => setLoadingChildren(false));
    API.get('/characters').then(res => {
      setCharactersLocked(res.data.locked || false);
      setAvailableCharacters(res.data.characters || []);
    }).catch(() => {});
  }, []); // eslint-disable-line

  // Pre-fill form if arriving from seasonal banner link
  useEffect(() => {
    const sid = searchParams.get('seasonalId');
    if (!sid) return;
    const h = HOLIDAYS.find(x => x.id === sid);
    if (!h) return;
    const rawThemes = searchParams.get('themes');
    if (rawThemes) setThemes(rawThemes.split(',').filter(Boolean));
    const t = searchParams.get('tone');
    if (t) setTone(t);
    const p = searchParams.get('prompt');
    if (p) setCustomPrompt(p);
    setSeasonalApplied(true);
    // Switch to Seasonal tab
    setActiveThemeTab(1);
  }, []); // eslint-disable-line

  // Pre-fill from Story-of-the-Night deep link (?autostart=1&childId=&themes=&tone=&interests=&learningTopic=)
  useEffect(() => {
    if (!searchParams.get('autostart')) return;
    const cid = searchParams.get('childId');
    if (cid) setSelectedChild(cid);
    const rawThemes = searchParams.get('themes');
    if (rawThemes) setThemes(rawThemes.split(',').filter(Boolean));
    const t = searchParams.get('tone');
    if (t) setTone(t);
    const rawInterests = searchParams.get('interests');
    if (rawInterests) setInterests(rawInterests.split(',').filter(Boolean));
    const lt = searchParams.get('learningTopic');
    if (lt) setLearningTopic(lt);
    setAutoStartPending(true);
  }, []); // eslint-disable-line

  // Fire handleGenerate once autostart is armed and children are loaded
  useEffect(() => {
    if (!autoStartPending || loadingChildren || autoStartFiredRef.current) return;
    autoStartFiredRef.current = true;
    setAutoStartPending(false);
    // handleGenerate is defined below; this runs later (deferred effect), so it's safe.
    // eslint-disable-next-line no-use-before-define
    handleGenerate();
  }, [autoStartPending, loadingChildren]); // eslint-disable-line

  const toggleInterest = (i) => setInterests(prev =>
    prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]
  );

  const toggleTheme = (t) => {
    if (!hasPaidPlan) {
      setThemes(prev => prev.includes(t) ? [] : [t]);
      return;
    }
    setThemes(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);
  };

  const toggleChildSelection = (childId) => {
    setSelectedChildren(prev =>
      prev.includes(childId) ? prev.filter(id => id !== childId) : [...prev, childId]
    );
  };

  const handleGenerate = async () => {
    if (multiChildMode) {
      if (selectedChildren.length < 2) return setError('Please select at least 2 children for a Family Story.');
    } else {
      if (!selectedChild) return setError('Please select a child profile first.');
    }
    setError(''); setGenerating(true);
    setGenProgress(0);
    setGenMsg(PROGRESS_STEPS[0].msg);

    // Animate progress independently of the API call
    let pct = 0;
    let msgIdx = 0;
    genProgressRef.current = setInterval(() => {
      const inc = pct < 55 ? 2.2 : pct < 80 ? 0.9 : 0.2;
      pct = Math.min(pct + inc, 92);
      setGenProgress(pct);
      const next = PROGRESS_STEPS.findIndex(s => s.pct > pct);
      const idx  = next === -1 ? PROGRESS_STEPS.length - 1 : next - 1;
      if (idx !== msgIdx) { msgIdx = idx; setGenMsg(PROGRESS_STEPS[idx].msg); }
    }, 400);

    try {
      const effectiveLearningTopic = customLearningTopic.trim() || learningTopic || undefined;
      const characterIds = selectedCharacterIds.length ? selectedCharacterIds : undefined;
      const body = multiChildMode
        ? { childIds: selectedChildren, interests: interests.length ? interests : undefined, themes, tone: tone || undefined, customPrompt, language, learningTopic: effectiveLearningTopic, characterIds }
        : { childId: selectedChild, interests, themes, tone: tone || undefined, customPrompt, language, learningTopic: effectiveLearningTopic, characterIds };
      const res = await API.post('/stories/generate', body);
      clearInterval(genProgressRef.current);

      // Server returns immediately with storyId — navigate now, StoryReaderPage polls
      trackStoryGenerated({ language, isMultiChild: multiChildMode });
      refreshLimits();

      if (res.data.pendingApproval) {
        navigate(`/dashboard/approval-queue?new=true`);
      } else {
        navigate(`/dashboard/story/${res.data.storyId}?new=true&generating=true`);
      }
    } catch (err) {
      clearInterval(genProgressRef.current);
      setGenProgress(0);
      const msg = err.response?.data?.message || 'Story generation failed. Please try again.';
      const limitHit = err.response?.data?.limitReached;
      if (limitHit) { setShowTopUp(true); }
      setError(msg);
    } finally { setGenerating(false); }
  };

  const selectedChildData = children.find(c => c._id === selectedChild);
  const selectedChildrenData = children.filter(c => selectedChildren.includes(c._id));
  const selectedLang = LANGUAGES.find(l => l.code === language);
  const selectedToneData = TONES.find(t => t.value === tone);

  // generateStory 403s a read_only co-parent. Say so up front rather than let
  // them choose a child, themes and a tone and fail at the last button.
  if (!canGenerateStories(user)) return (
    <DashboardLayout title="Generate a Story">
      <div className="text-center py-5">
        <div style={{ fontSize: '44px', marginBottom: '12px' }}>👀</div>
        <h5 className="fw-bold mb-2" style={{ color: 'var(--text-main)' }}>You have view-only access</h5>
        <p className="mb-4" style={{ color: 'var(--text-muted2)', maxWidth: 440, margin: '0 auto 24px' }}>
          Your family access level lets you read the stories in this library, but not create new ones.
          Ask the account owner to change your role to <strong>View &amp; Generate</strong> if you'd like to write stories too.
        </p>
        <Link to="/dashboard/library" className="btn btn-dream rounded-pill px-4">
          <i className="bi bi-book-half me-2"></i>Go to the library
        </Link>
      </div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout title="Generate a Story">

      {/* Page header */}
      <div className="rounded-4 p-4 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-3"
        style={{background:'var(--purple-section)', border:'1px solid var(--border-purple2)'}}>
        <div>
          <h4 className="fw-bold mb-1" style={{color:'var(--text-main)', fontFamily:"'Fredoka One',cursive"}}>
            🪄 Generate Tonight's Story
          </h4>
          <p className="mb-0 small" style={{color:'#6f42c1'}}>
            Dreamy Bedtime crafts a unique, personalised bedtime story in ~15 seconds — complete with a moral lesson and magical illustration.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <span className="badge rounded-pill px-3 py-2" style={{background:'rgba(111,66,193,0.15)', color:'#6f42c1', fontSize:'13px'}}>
            <i className="bi bi-magic me-1"></i>{usage.stories}/{limits.stories} stories used
          </span>
          {remaining.stories <= 3 && remaining.stories > 0 && (
            <span className="badge rounded-pill px-3 py-2" style={{background:'var(--orange-section)', color:'var(--text-orange)', fontSize:'13px', border:'1px solid var(--border-orange)'}}>
              ⚠️ {remaining.stories} left this month
            </span>
          )}
          {storiesOut && (
            <button className="btn btn-sm rounded-pill fw-semibold px-3"
              style={{background:'#ea580c', color:'white', fontSize:'13px', border:'none'}}
              onClick={() => setShowTopUp(true)}>
              ⚡ Buy more stories
            </button>
          )}
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-lg-9 col-xl-8">
          {error && (
            <div className="alert alert-danger mb-4 rounded-3 d-flex align-items-center gap-2">
              <i className="bi bi-exclamation-circle-fill"></i>{error}
            </div>
          )}

          {showThemeVoting && selectedChild && (
            <ThemeVotingCard
              childName={children.find(c => c._id === selectedChild)?.name || 'your child'}
              onThemeSelected={(theme) => {
                setThemes([theme]);
                setShowThemeVoting(false);
              }}
              onSkip={() => setShowThemeVoting(false)}
            />
          )}

          {loadingChildren ? (
            <div className="text-center py-5">
              <div className="spinner-border" style={{color:'#6f42c1', width:'3rem', height:'3rem'}}></div>
              <p className="text-muted mt-3">Loading your children's profiles…</p>
            </div>
          ) : children.length === 0 ? (
            <div className="card border-0 shadow-sm p-5 text-center" style={{borderRadius:'24px'}}>
              <div style={{fontSize:'64px', marginBottom:'16px'}}>👶</div>
              <h5 className="fw-bold mb-2" style={{color:'var(--text-main)'}}>Create a child profile first</h5>
              <p className="text-muted mb-4">Dreamy Bedtime needs to know your child's name and age to write a personalised story.</p>
              <Link to="/dashboard/children" className="btn btn-dream rounded-pill px-5 fw-semibold mx-auto" style={{width:'fit-content'}}>
                <i className="bi bi-plus-circle me-2"></i>Create a Profile
              </Link>
            </div>
          ) : (
            <div className="card border-0 shadow-sm" style={{borderRadius:'24px', overflow:'hidden'}}>
              <div style={{height:'4px', background:'linear-gradient(90deg,#6f42c1,#9b6fe8,#f9a8d4)'}}></div>
              <div className="p-4 p-lg-5">

                {/* ── Seasonal suggestion banner ── */}
                {seasonalHoliday && (
                  <div className="rounded-3 p-3 mb-4 d-flex align-items-center justify-content-between flex-wrap gap-2"
                    style={{ background: seasonalHoliday.gradient, position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: '-10px', right: '-10px', width: '70px', height: '70px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <div className="d-flex align-items-center gap-2">
                      <span style={{ fontSize: '28px', lineHeight: 1 }}>{seasonalHoliday.emoji}</span>
                      <div>
                        <div className="fw-bold text-white" style={{ fontSize: '14px' }}>
                          {seasonal.active ? `${seasonalHoliday.name} is here!` : `${seasonalHoliday.name} in ${seasonalDaysAway} day${seasonalDaysAway === 1 ? '' : 's'}!`}
                        </div>
                        <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '12px' }}>{seasonalHoliday.description}</div>
                      </div>
                    </div>
                    {!seasonalApplied ? (
                      <button
                        type="button"
                        className="btn btn-sm fw-semibold rounded-pill px-3 flex-shrink-0"
                        style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.4)' }}
                        onClick={() => {
                          setThemes(seasonalHoliday.themes);
                          setTone(seasonalHoliday.tone);
                          setCustomPrompt(seasonalHoliday.suggestedPrompt);
                          setActiveThemeTab(1);
                          setSeasonalApplied(true);
                        }}
                      >
                        <i className="bi bi-magic me-1"></i>Apply {seasonalHoliday.name} theme
                      </button>
                    ) : (
                      <span className="badge rounded-pill fw-semibold px-3 py-2 flex-shrink-0"
                        style={{ background: 'rgba(255,255,255,0.25)', color: 'white', fontSize: '12px' }}>
                        <i className="bi bi-check2 me-1"></i>Theme applied!
                      </span>
                    )}
                  </div>
                )}

                {/* ── Step 1: Choose child(ren) ── */}
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{width:'28px', height:'28px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>1</span>
                    <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>Whose story is this?</h6>
                  </div>
                  <p className="text-muted small mb-3 ms-5">The story will be personalised for the child you choose.</p>

                  {/* Multi-child toggle — only shown when ≥2 children and paid plan */}
                  {children.length >= 2 && (
                    <div className="ms-1 mb-3">
                      {hasPaidPlan ? (
                        <div className="d-flex gap-2">
                          <button type="button"
                            className={`btn btn-sm rounded-pill px-3 ${!multiChildMode ? 'btn-dream' : 'btn-outline-secondary'}`}
                            onClick={() => { setMultiChildMode(false); setSelectedChildren([]); }}>
                            <i className="bi bi-person me-1"></i>One Child
                          </button>
                          <button type="button"
                            className={`btn btn-sm rounded-pill px-3 ${multiChildMode ? 'btn-dream' : 'btn-outline-secondary'}`}
                            onClick={() => { setMultiChildMode(true); setSelectedChild(''); }}>
                            <i className="bi bi-people-fill me-1"></i>Family Story ✨
                          </button>
                        </div>
                      ) : (
                        <div className="p-3 rounded-3 d-flex align-items-center gap-3"
                          style={{background:'var(--item-bg)', border:'1.5px dashed var(--border-purple2)'}}>
                          <span style={{fontSize:'24px'}}>👨‍👩‍👧‍👦</span>
                          <div>
                            <div className="fw-semibold" style={{color:'var(--text-main)', fontSize:'14px'}}>Family Story — all siblings as heroes</div>
                            <div className="text-muted small">Generate one story featuring all your children together.</div>
                          </div>
                          <Link to="/pricing" className="btn btn-sm rounded-pill ms-auto px-3 fw-semibold flex-shrink-0"
                            style={{background:'#6f42c1', color:'white', border:'none', fontSize:'12px'}}>
                            Unlock
                          </Link>
                        </div>
                      )}
                    </div>
                  )}

                  {multiChildMode ? (
                    <>
                      <p className="text-muted small mb-3 ms-1">
                        <i className="bi bi-info-circle me-1"></i>Select 2 or more children — every sibling will be a main character in the story.
                      </p>
                      <div className="d-flex flex-wrap gap-3 ms-1">
                        {children.map(child => {
                          const isSelected = selectedChildren.includes(child._id);
                          return (
                            <div key={child._id} onClick={() => toggleChildSelection(child._id)}
                              className="card border-2 p-3 text-center"
                              style={{borderRadius:'16px', minWidth:'110px', cursor:'pointer', transition:'all 0.15s',
                                borderColor: isSelected ? '#6f42c1' : (dm ? '#3d2a72' : '#dee2e6'),
                                background: isSelected ? 'var(--purple-section)' : 'var(--card-bg)',
                                boxShadow: isSelected ? '0 4px 12px rgba(111,66,193,0.2)' : 'none',
                                transform: isSelected ? 'translateY(-2px)' : 'none'}}>
                              <div style={{fontSize:'32px', lineHeight:1, marginBottom:'6px'}}>{child.avatar}</div>
                              <div className="fw-bold small" style={{color:'var(--text-main)'}}>{child.name}</div>
                              <div style={{fontSize:'11px', color:'var(--text-soft)'}}>Age {child.age}</div>
                              {isSelected
                                ? <i className="bi bi-check-circle-fill mt-1" style={{color:'#6f42c1', fontSize:'14px'}}></i>
                                : <i className="bi bi-circle mt-1" style={{color:'#ccc', fontSize:'14px'}}></i>
                              }
                            </div>
                          );
                        })}
                      </div>
                      {selectedChildren.length >= 2 && (
                        <div className="mt-3 ms-1 p-2 rounded-3 d-inline-flex align-items-center gap-2"
                          style={{background:'var(--purple-section)', border:'1px solid var(--border-purple2)'}}>
                          <span>👨‍👩‍👧‍👦</span>
                          <span className="small fw-semibold" style={{color:'#6f42c1'}}>
                            Family story for: {selectedChildrenData.map(c => c.name).join(', ')}
                          </span>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="d-flex flex-wrap gap-3 ms-1">
                      {children.map(child => (
                        <div key={child._id} onClick={() => { setSelectedChild(child._id); if (themes.length === 0) setShowThemeVoting(true); }}
                          className="card border-2 p-3 text-center"
                          style={{borderRadius:'16px', minWidth:'110px', cursor:'pointer', transition:'all 0.15s',
                            borderColor: selectedChild === child._id ? '#6f42c1' : (dm ? '#3d2a72' : '#dee2e6'),
                            background: selectedChild === child._id ? 'var(--purple-section)' : 'var(--card-bg)',
                            boxShadow: selectedChild === child._id ? '0 4px 12px rgba(111,66,193,0.2)' : 'none',
                            transform: selectedChild === child._id ? 'translateY(-2px)' : 'none'}}>
                          <div style={{fontSize:'32px', lineHeight:1, marginBottom:'6px'}}>{child.avatar}</div>
                          <div className="fw-bold small" style={{color:'var(--text-main)'}}>{child.name}</div>
                          <div style={{fontSize:'11px', color:'var(--text-soft)'}}>Age {child.age}</div>
                          {selectedChild === child._id && (
                            <i className="bi bi-check-circle-fill mt-1" style={{color:'#6f42c1', fontSize:'14px'}}></i>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ── Step 2: Theme ── */}
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{width:'28px', height:'28px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>2</span>
                    <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>
                      Story theme <span className="text-muted fw-normal" style={{fontSize:'13px'}}>(optional)</span>
                    </h6>
                  </div>
                  <p className="text-muted small mb-3 ms-5">
                    {hasPaidPlan ? 'Pick one or more — they\'ll all be woven naturally into the story.' : 'Choose a life lesson to weave into the story.'}
                  </p>

                  {hasPaidPlan ? (
                    <>
                      {/* Category tabs */}
                      <div className="d-flex flex-wrap gap-2 mb-3 ms-1">
                        {THEME_CATEGORIES.map((cat, i) => (
                          <button key={i} type="button"
                            className={`btn btn-sm rounded-pill ${activeThemeTab === i ? 'btn-dream' : 'btn-outline-secondary'}`}
                            onClick={() => setActiveThemeTab(i)}>
                            {cat.icon} {cat.label}
                            <span className="ms-1 d-none d-md-inline text-muted fw-normal" style={{fontSize:'11px'}}>— {cat.desc}</span>
                          </button>
                        ))}
                      </div>
                      <div className="d-flex flex-wrap gap-2 ms-1">
                        {THEME_CATEGORIES[activeThemeTab].themes.map(t => (
                          <button key={t} type="button" onClick={() => toggleTheme(t)}
                            className={`btn btn-sm rounded-pill ${themes.includes(t) ? 'btn-dream' : 'btn-outline-secondary'}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                      {themes.length > 0 && (
                        <div className="mt-3 ms-1 d-flex flex-wrap align-items-center gap-2">
                          <span className="text-muted small">Selected:</span>
                          {themes.map(t => (
                            <span key={t} className="badge rounded-pill d-flex align-items-center gap-1"
                              style={{background:'#6f42c1', color:'white', padding:'5px 10px'}}>
                              {t}
                              <button type="button" className="btn-close btn-close-white" style={{fontSize:'8px'}}
                                onClick={() => toggleTheme(t)}></button>
                            </span>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div className="d-flex flex-wrap gap-2 mb-2 ms-1">
                        {FREE_THEMES.map(t => (
                          <button key={t} type="button" onClick={() => toggleTheme(t)}
                            className={`btn btn-sm rounded-pill ${themes.includes(t) ? 'btn-dream' : 'btn-outline-secondary'}`}>
                            {t}
                          </button>
                        ))}
                      </div>
                      <p className="text-muted small ms-1 mb-0">
                        <i className="bi bi-lock-fill me-1"></i>
                        <Link to="/pricing" style={{color:'#6f42c1'}}>Upgrade to Basic</Link> for 30+ seasonal, emotional &amp; multi-theme control.
                      </p>
                    </>
                  )}
                </div>

                {/* ── Step 3: Tone (paid only) ── */}
                {hasPaidPlan && (
                  <div className="mb-5">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                        style={{width:'28px', height:'28px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>3</span>
                      <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>
                        Story tone <span className="text-muted fw-normal" style={{fontSize:'13px'}}>(optional — overrides profile default)</span>
                      </h6>
                    </div>
                    <p className="text-muted small mb-3 ms-5">Sets the overall mood. The profile default is used if nothing is selected.</p>
                    <div className="row g-2 ms-1">
                      {TONES.map(t => (
                        <div className="col-6 col-sm-4 col-md-3" key={t.value}>
                          <div onClick={() => setTone(prev => prev === t.value ? '' : t.value)}
                            className="p-2 text-center rounded-3"
                            style={{cursor:'pointer', transition:'all 0.15s',
                              border: tone === t.value ? '2px solid #6f42c1' : `2px solid ${dm ? '#3d2a72' : '#dee2e6'}`,
                              background: tone === t.value ? 'var(--purple-section)' : 'var(--card-bg)',
                              boxShadow: tone === t.value ? '0 4px 12px rgba(111,66,193,0.15)' : 'none'}}>
                            <div style={{fontSize:'22px', lineHeight:1, marginBottom:'4px'}}>{t.icon}</div>
                            <div className="fw-semibold" style={{fontSize:'12px', color:'var(--text-main)'}}>{t.value.charAt(0).toUpperCase()+t.value.slice(1)}</div>
                            <div style={{fontSize:'10px', color:'var(--text-soft)', marginTop:'1px'}}>{t.desc}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Step 4 / 3: Interests ── */}
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{width:'28px', height:'28px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>
                      {hasPaidPlan ? '4' : '3'}
                    </span>
                    <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>
                      Extra interests <span className="text-muted fw-normal" style={{fontSize:'13px'}}>(optional — overrides profile)</span>
                    </h6>
                  </div>
                  <p className="text-muted small mb-3 ms-5">
                    Add topics you'd like in tonight's story specifically. Your profile interests are always used as a base.
                  </p>
                  <div className="d-flex flex-wrap gap-2 ms-1">
                    {INTERESTS.map(i => (
                      <button key={i} type="button" onClick={() => toggleInterest(i)}
                        className={`btn btn-sm rounded-pill ${interests.includes(i) ? 'btn-dream' : 'btn-outline-secondary'}`}>
                        {i}
                      </button>
                    ))}
                  </div>
                </div>

                {/* ── Learning Topic step (paid only) ── */}
                {hasPaidPlan ? (
                  <div className="mb-5">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                        style={{width:'28px', height:'28px', background:'#059669', fontSize:'13px', flexShrink:0}}>5</span>
                      <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>
                        Learning topic <span className="text-muted fw-normal" style={{fontSize:'13px'}}>(optional)</span>
                        <span className="ms-2 badge rounded-pill" style={{background:'#dcfce7', color:'#15803d', fontSize:'11px', verticalAlign:'middle'}}>🎓 Educational</span>
                      </h6>
                    </div>
                    <p className="text-muted small mb-3 ms-5">
                      Pick a real-world topic and it will be woven naturally into the story — learning through adventure, not a classroom.
                    </p>

                    {/* Category tabs */}
                    <div className="d-flex flex-wrap gap-2 mb-3 ms-1">
                      {LEARNING_CATEGORIES.map((cat, i) => (
                        <button key={i} type="button"
                          className={`btn btn-sm rounded-pill ${learningTab === i ? 'btn-success' : 'btn-outline-secondary'}`}
                          style={learningTab === i ? {background:'#059669', border:'none'} : {}}
                          onClick={() => setLearningTab(i)}>
                          {cat.icon} {cat.label}
                        </button>
                      ))}
                    </div>

                    {/* Topic pills */}
                    <div className="d-flex flex-wrap gap-2 ms-1 mb-3">
                      {LEARNING_CATEGORIES[learningTab].topics.map(t => (
                        <button key={t} type="button"
                          onClick={() => { setLearningTopic(prev => prev === t ? '' : t); setCustomLearningTopic(''); }}
                          className="btn btn-sm rounded-pill"
                          style={{
                            background: learningTopic === t ? '#059669' : 'var(--card-bg)',
                            color: learningTopic === t ? 'white' : 'var(--text-main)',
                            border: learningTopic === t ? '1.5px solid #059669' : `1.5px solid ${dm ? 'rgba(74,222,128,0.3)' : '#d1fae5'}`,
                            transition: 'all 0.15s',
                          }}>
                          {t}
                        </button>
                      ))}
                    </div>

                    {/* Custom topic */}
                    <div className="ms-1">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Or type your own… e.g. How rainbows form, Multiplication, Road safety"
                        value={customLearningTopic}
                        onChange={e => { setCustomLearningTopic(e.target.value); setLearningTopic(''); }}
                        style={{borderRadius:'12px', fontSize:'14px'}}
                        maxLength={80}
                      />
                    </div>

                    {(learningTopic || customLearningTopic.trim()) && (
                      <div className="mt-3 ms-1 p-2 rounded-3 d-inline-flex align-items-center gap-2"
                        style={{background:'var(--green-section)', border:'1px solid var(--border-green)'}}>
                        <i className="bi bi-mortarboard-fill" style={{color:'#059669'}}></i>
                        <span className="small fw-semibold" style={{color:'var(--text-green)'}}>
                          Teaching: {customLearningTopic.trim() || learningTopic}
                        </span>
                        <button type="button" className="btn-close" style={{fontSize:'8px'}}
                          onClick={() => { setLearningTopic(''); setCustomLearningTopic(''); }}></button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="mb-5 ms-1">
                    <div className="p-3 rounded-3 d-flex align-items-center gap-3"
                      style={{background:'var(--green-section)', border:'1.5px dashed var(--border-green)'}}>
                      <span style={{fontSize:'28px'}}>🎓</span>
                      <div>
                        <div className="fw-semibold" style={{color:'var(--text-green)', fontSize:'14px'}}>Real-world Learning Topics</div>
                        <div className="text-muted small">Weave planets, fractions, kindness and more into tonight's story.</div>
                      </div>
                      <Link to="/pricing" className="btn btn-sm rounded-pill ms-auto px-3 fw-semibold flex-shrink-0"
                        style={{background:'#059669', color:'white', border:'none', fontSize:'12px'}}>
                        Unlock
                      </Link>
                    </div>
                  </div>
                )}

                {/* ── Step 5 / 4: Language ── */}
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{width:'28px', height:'28px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>
                      {hasPaidPlan ? '6' : '4'}
                    </span>
                    <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>Story language</h6>
                  </div>
                  <p className="text-muted small mb-3 ms-5">
                    The full story — title, content, and moral — will be written in the selected language. Great for bilingual families.
                  </p>
                  <div className="d-flex flex-wrap gap-2 ms-1">
                    {LANGUAGES.map(lang => (
                      <button key={lang.code} type="button"
                        onClick={() => setLanguage(lang.code)}
                        className={`btn btn-sm rounded-pill ${language === lang.code ? 'btn-dream' : 'btn-outline-secondary'}`}>
                        {lang.flag} {lang.label}
                      </button>
                    ))}
                  </div>
                  {language !== 'en' && (
                    <div className="mt-3 ms-1 p-2 rounded-3 d-inline-flex align-items-center gap-2"
                      style={{background:'var(--yellow-section)', border:'1px solid var(--border-yellow)'}}>
                      <i className="bi bi-translate" style={{color:'#d97706'}}></i>
                      <span className="small" style={{color:'var(--text-orange2)'}}>
                        Story will be written entirely in {selectedLang?.label}. Your child's name stays unchanged.
                      </span>
                    </div>
                  )}
                </div>

                {/* ── Story Characters (paid) ── */}
                {hasPaidPlan && (
                  <div className="mb-5">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                        style={{width:'28px', height:'28px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>7</span>
                      <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>
                        Story Characters <span className="text-muted fw-normal" style={{fontSize:'13px'}}>(optional)</span>
                      </h6>
                    </div>
                    <p className="text-muted small mb-3 ms-5">
                      Add recurring pets or friends to appear in this story.{' '}
                      <Link to="/dashboard/characters" style={{color:'#6f42c1'}}>Manage characters</Link>
                    </p>
                    {availableCharacters.length === 0 ? (
                      <div className="ms-1 p-3 rounded-3 d-flex align-items-center gap-3"
                        style={{background:'var(--item-bg)', border:'1px dashed var(--border-purple)'}}>
                        <span style={{fontSize:24}}>🐾</span>
                        <div>
                          <div className="small fw-semibold" style={{color:'var(--text-main)'}}>No characters yet</div>
                          <Link to="/dashboard/characters" className="small" style={{color:'#6f42c1'}}>Create a pet or friend →</Link>
                        </div>
                      </div>
                    ) : (
                      <div className="d-flex flex-wrap gap-2 ms-1">
                        {availableCharacters.map(char => {
                          const sel = selectedCharacterIds.includes(char._id);
                          return (
                            <button key={char._id} type="button"
                              onClick={() => setSelectedCharacterIds(prev =>
                                sel ? prev.filter(id => id !== char._id) : [...prev, char._id]
                              )}
                              style={{
                                background: sel ? 'var(--purple-section-2)' : 'var(--card-bg)',
                                border: sel ? '1.5px solid #6f42c1' : '1.5px solid var(--border-purple)',
                                borderRadius: 20, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                                color: sel ? '#6f42c1' : 'var(--text-body)', fontSize: 13, fontWeight: sel ? 600 : 400,
                                transition: 'all 0.15s',
                              }}>
                              <span style={{fontSize:18}}>{char.emoji}</span>
                              {char.name}
                              {sel && <i className="bi bi-check-circle-fill" style={{fontSize:11, marginLeft:2}}></i>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* ── Step 6 / 5 / 8: Special request ── */}
                <div className="mb-5">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <span className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                      style={{width:'28px', height:'28px', background:'#6f42c1', fontSize:'13px', flexShrink:0}}>
                      {hasPaidPlan ? '8' : '5'}
                    </span>
                    <h6 className="fw-bold mb-0" style={{color:'var(--text-main)', fontSize:'16px'}}>
                      Special request <span className="text-muted fw-normal" style={{fontSize:'13px'}}>(optional)</span>
                    </h6>
                  </div>
                  <p className="text-muted small mb-3 ms-5">
                    Any specific details you'd like — a setting, a character name, or a funny twist.
                  </p>
                  <textarea className="form-control ms-1" rows="2"
                    placeholder="e.g. Set in outer space, include a talking dog named Biscuit, make it really funny…"
                    value={customPrompt} onChange={e => setCustomPrompt(e.target.value)}
                    style={{borderRadius:'12px', resize:'none'}}></textarea>
                </div>

                {/* ── Story Preview card ── */}
                {(multiChildMode ? selectedChildren.length >= 2 : !!selectedChildData) && (
                  <div className="mb-4 p-4 rounded-4"
                    style={{background:'var(--purple-section)', border:'2px solid var(--border-purple2)'}}>
                    <div className="d-flex align-items-center gap-3 mb-3">
                      {multiChildMode ? (
                        <div className="d-flex" style={{fontSize:'28px', lineHeight:1}}>
                          {selectedChildrenData.map((c, i) => (
                            <span key={c._id} style={{marginLeft: i > 0 ? '-6px' : 0}}>{c.avatar}</span>
                          ))}
                        </div>
                      ) : (
                        <div style={{fontSize:'36px', lineHeight:1}}>{selectedChildData.avatar}</div>
                      )}
                      <div>
                        <div className="fw-bold" style={{color:'var(--text-main)', fontSize:'16px'}}>
                          {multiChildMode
                            ? `${selectedChildrenData.map(c => c.name).join(' & ')}'s Family Story is ready`
                            : `${selectedChildData.name}'s story is ready to generate`}
                        </div>
                        <div className="text-muted small">Here's what we'll use:</div>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {multiChildMode ? (
                        <span className="badge rounded-pill" style={{background:'#e8d5ff', color:'#6f42c1', border:'1px solid #d4b8ff', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                          <i className="bi bi-people-fill me-1"></i>{selectedChildrenData.length} siblings · ages {Math.min(...selectedChildrenData.map(c=>c.age))}–{Math.max(...selectedChildrenData.map(c=>c.age))}
                        </span>
                      ) : (
                        <span className="badge rounded-pill" style={{background:'var(--card-bg)', color:'var(--text-muted2)', border:'1px solid var(--border-purple)', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                          <i className="bi bi-person me-1"></i>Age {selectedChildData.age}
                        </span>
                      )}
                      <span className="badge rounded-pill" style={{background:'var(--card-bg)', color:'var(--text-muted2)', border:'1px solid var(--border-purple)', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                        <i className="bi bi-palette me-1"></i>{selectedToneData?.icon || '✨'} {(tone || (!multiChildMode && selectedChildData?.storyPreferences?.tone) || 'magical').charAt(0).toUpperCase()+(tone || (!multiChildMode && selectedChildData?.storyPreferences?.tone) || 'magical').slice(1)} tone
                      </span>
                      {!multiChildMode && (
                        <span className="badge rounded-pill" style={{background:'var(--card-bg)', color:'var(--text-muted2)', border:'1px solid var(--border-purple)', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                          <i className="bi bi-clock me-1"></i>{selectedChildData.storyPreferences?.length || 'medium'} length
                        </span>
                      )}
                      {themes.length > 0 && (
                        <span className="badge rounded-pill" style={{background:'var(--card-bg)', color:'var(--text-muted2)', border:'1px solid var(--border-purple)', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                          <i className="bi bi-lightbulb me-1"></i>{themes.join(', ')}
                        </span>
                      )}
                      {!multiChildMode && interests.length > 0 && (
                        <span className="badge rounded-pill" style={{background:'var(--card-bg)', color:'var(--text-muted2)', border:'1px solid var(--border-purple)', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                          <i className="bi bi-stars me-1"></i>{interests.join(', ')}
                        </span>
                      )}
                      {(customLearningTopic.trim() || learningTopic) && (
                        <span className="badge rounded-pill" style={{background:'#f0fdf4', color:'#15803d', border:'1px solid #86efac', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                          <i className="bi bi-mortarboard-fill me-1"></i>{customLearningTopic.trim() || learningTopic}
                        </span>
                      )}
                      {selectedCharacterIds.length > 0 && (
                        <span className="badge rounded-pill" style={{background:'var(--purple-section)', color:'#6f42c1', border:'1px solid var(--border-purple)', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                          <span className="me-1">🐾</span>
                          {availableCharacters.filter(c => selectedCharacterIds.includes(c._id)).map(c => c.name).join(', ')}
                        </span>
                      )}
                      <span className="badge rounded-pill" style={{background:'var(--card-bg)', color:'var(--text-muted2)', border:'1px solid var(--border-purple)', fontWeight:500, fontSize:'12px', padding:'5px 10px'}}>
                        {selectedLang?.flag} {selectedLang?.label}
                      </span>
                    </div>
                  </div>
                )}

                {/* ── Limit reached banner ── */}
                {storiesOut && (
                  <div className="mb-4 p-4 rounded-4 d-flex align-items-center gap-3"
                    style={{background:'var(--orange-section)', border:'2px solid var(--border-orange)'}}>
                    <span style={{fontSize:'28px', flexShrink:0}}>⚡</span>
                    <div className="flex-grow-1">
                      <div className="fw-bold mb-1" style={{color:'var(--text-orange)', fontSize:'15px'}}>You've used all your stories this month</div>
                      <div style={{color:'var(--text-orange2)', fontSize:'13px'}}>Buy a Story Pack to keep generating, or wait until next month for your limit to reset.</div>
                    </div>
                    <button className="btn rounded-pill fw-bold px-4 flex-shrink-0"
                      style={{background:'#ea580c', color:'white', border:'none', fontSize:'14px'}}
                      onClick={() => setShowTopUp(true)}>
                      Buy Pack
                    </button>
                  </div>
                )}

                {/* ── Generate button ── */}
                <button className="btn btn-dream btn-lg w-100 rounded-pill fw-bold py-3"
                  onClick={handleGenerate} disabled={generating || storiesOut}
                  style={{fontSize:'17px', letterSpacing:'0.3px', opacity: storiesOut ? 0.5 : 1}}>
                  {generating ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Writing the story…</>
                  ) : multiChildMode ? (
                    <><i className="bi bi-people-fill me-2"></i>Generate Family Story ✨</>
                  ) : (
                    <><i className="bi bi-magic me-2"></i>Generate Tonight's Story</>
                  )}
                </button>

                {generating && (
                  <div className="mt-4">
                    {/* Animated progress bar */}
                    <div style={{background:'#e8d5ff', borderRadius:'999px', height:'10px', marginBottom:'10px', overflow:'hidden'}}>
                      <div style={{
                        height:'100%', borderRadius:'999px',
                        background:'linear-gradient(90deg,#6f42c1,#9b6fe8)',
                        width:`${genProgress}%`,
                        transition:'width 0.4s ease',
                      }}/>
                    </div>
                    <p className="text-center fw-semibold small mb-3" style={{color:'#6f42c1', minHeight:'18px'}}>
                      {genMsg}
                    </p>
                    <div className="d-flex justify-content-center gap-4">
                      {[['✍️','Writing story'],['🎨','Illustrating'],['💫','Adding lesson']].map(([icon, label]) => (
                        <div key={label} className="text-center">
                          <div style={{fontSize:'20px'}}>{icon}</div>
                          <div className="small text-muted" style={{fontSize:'11px'}}>{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!generating && (
                  <p className="text-center text-muted small mt-3 mb-0">
                    <i className="bi bi-shield-check me-1"></i>
                    Safe for children · Age-appropriate · Unique every time
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
      <TopUpModal
        show={showTopUp}
        onClose={() => setShowTopUp(false)}
        packType="stories"
        planName={entitledPlan(user)}
      />
    </DashboardLayout>
  );
};

export default GenerateStoryPage;
